<template>
  <v-sheet fluid class="pa-0 ma-0">
    <loading-spinner v-if="isLoading" class="py-10" />

    <v-card v-else class="ma-0 pa-0">
      <v-card-title class="ma-0 pa-3">
        <img
          :src="'/images/' + variable.category + '-icon-128.png'"
          :title="variable.category"
          style="height: 2.5em;"
          class="px-2"
          @error="missingDomainFallback"
        />
        <span class="ml-2 text-wrap"> {{ getVarName() }} </span>
        <span v-if="showDatasetName" class="ml-2 text-wrap">
          | {{ dataset.study_name }}
        </span>
        <v-spacer></v-spacer>
        <v-tooltip bottom color="primary">
          <template v-slot:activator="{ on: tooltip }">
            <v-btn
              icon
              class="white--text ma-0 pa-0"
              @click="close"
              v-on="{ ...tooltip }"
            >
              <v-icon color="black" center large class="pr-2">close</v-icon>
            </v-btn>
          </template>
          <span>Close</span>
        </v-tooltip>
      </v-card-title>

      <v-card-text class="ma-0 pa-0">
        <div v-if="getVarName() != variable.description" class="pl-5 pb-3">
          {{ variable.description }}
        </div>

        <v-container fluid class="pa-0 ma-0">
          <v-row class="pa-0 ma-0 mx-6 pb-2" justify="center" align="center">
            <v-tooltip v-if="dataset.longitudinal" bottom color="primary">
              <template v-slot:activator="{ on: tooltip }">
                <span v-on="{ ...tooltip }">
                  <v-checkbox
                    v-model="hideOutlierVisits"
                    label="Hide outlier visits"
                    class="pa-0 ma-0"
                    dense
                    hide-details
                    @click="!hideOutlierVisits"
                  />
                </span>
              </template>
              <span>Hide visit(s) with less than 10% of subjects</span>
            </v-tooltip>

            <v-divider
              v-if="
                dataset.longitudinal && variable.data_category == 'Continuous'
              "
              class="ml-2 my-0"
              vertical
              inset
            ></v-divider>

            <span
              v-if="
                dataset.longitudinal && variable.data_category == 'Continuous'
              "
              class="subtitle-1 ml-2 black--text"
              >Show:</span
            >

            <v-checkbox
              v-if="
                dataset.longitudinal && variable.data_category == 'Continuous'
              "
              v-model="showViolinChart"
              label="Violin chart"
              class="pa-0 ma-0 ml-2"
              dense
              hide-details
              @click="!showViolinChart"
            />

            <v-checkbox
              v-if="
                dataset.longitudinal && variable.data_category == 'Continuous'
              "
              v-model="showBoxChart"
              label="Box chart"
              class="pa-0 ma-0 ml-2"
              dense
              hide-details
              @click="!showBoxChart"
            />

            <v-checkbox
              v-if="
                dataset.longitudinal && variable.data_category == 'Continuous'
              "
              v-model="showLineChart"
              label="Line chart"
              class="pa-0 ma-0 ml-2"
              dense
              hide-details
              @click="!showLineChart"
            />

            <v-checkbox
              v-if="
                dataset.longitudinal && variable.data_category == 'Continuous'
              "
              v-model="showOutliers"
              label="Outliers"
              class="pa-0 ma-0 ml-2"
              dense
              hide-details
              :disabled="!showViolinChart && !showBoxChart"
              @click="!showOutliers"
            />

            <v-checkbox
              v-if="
                dataset.longitudinal && variable.data_category == 'Continuous'
              "
              v-model="showYAxisFromZero"
              label="Y-axis from zero"
              class="pa-0 ma-0 ml-2"
              dense
              hide-details
              @click="!showYAxisFromZero"
            />
          </v-row>

          <v-row class="pa-0 ma-0 pr-4">
            <v-col cols="12" class="pa-0 ma-0">
              <PlotlyChart :data="chartData" :layout="chartLayout" />
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>
    </v-card>
  </v-sheet>
</template>

<script>
import { mapActions } from 'vuex';
import { actions } from '@/store/modules/datasetManager/types';
import { colors } from '@/utils/colors';
import { sortVisitEvents, missingDomainFallback } from '@/utils/helpers';
import { extent, mean } from 'd3-array';
import PlotlyChart from '@/components/common/PlotlyChart.vue';

export default {
  components: {
    PlotlyChart,
  },
  props: {
    variable: {
      type: Object,
      required: true,
    },
    dataset: {
      type: Object,
      required: true,
    },
    showDatasetName: {
      type: Boolean,
      default: false,
    },
    useLongScaleNames: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isLoading: true,
      lastVarAndDataset: '',
      data: null,
      cache: {},
      visits: null,
      colors: colors,
      chartData: [],
      chartLayout: {},
      // Longitudinal
      hideOutlierVisits: false,
      // Longitudinal continuous
      showViolinChart: true,
      showBoxChart: false,
      showLineChart: false,
      showOutliers: true,
      showYAxisFromZero: true,
      missingDomainFallback: missingDomainFallback,
    };
  },
  computed: {
    xAxisLabel() {
      if (this.dataset.longitudinal) {
        return 'Visits / Time';
      } else {
        return this.getVarName();
      }
    },
    yAxisLabel() {
      if (this.dataset.longitudinal) {
        return this.getVarName();
      } else {
        return 'Number of subjects';
      }
    },
  },
  watch: {
    dataset(d) {
      this.checkForUpdate(this.variable, d);
    },
    variable(v) {
      this.checkForUpdate(v, this.dataset);
    },
    hideOutlierVisits() {
      if (this.hideOutlierVisits) {
        var cutoff = 0.1 * this.dataset.num_subjects;

        this.visits = sortVisitEvents(
          Object.entries(this.data)
            .filter(([_, data]) => data.length >= cutoff)
            .map(([visit]) => visit)
        );
      } else {
        this.visits = sortVisitEvents(Object.keys(this.data));
      }

      if (this.variable.data_category == 'Continuous') {
        this.cache = {
          box: {
            x: [],
            y: [],
          },
          line: {
            x: [],
            y: [],
          },
        };

        for (const v of this.visits) {
          let d = this.data[v];
          this.cache['box']['x'].push(...Array(d.length).fill(v));
          this.cache['box']['y'].push(...d);
          this.cache['line']['x'].push(v);
          this.cache['line']['y'].push(mean(d));
        }
      } else {
        let groups = {};

        for (const v of this.visits) {
          for (const d of this.data[v]) {
            if (!groups[d]) {
              groups[d] = [];
            }
            let entry = groups[d].find(e => e.visit_event == v);
            if (entry) {
              entry.count += 1;
            } else {
              groups[d].push({ visit_event: v, count: 1 });
            }
          }
        }

        for (const [k, entries] of Object.entries(groups)) {
          this.cache[k] = {
            x: entries.map(e => e.visit_event),
            y: entries.map(e => e.count),
          };
        }
      }

      this.renderChart();
    },
    highlightSetVisits() {
      this.renderChart();
    },
    showViolinChart() {
      this.renderChart();
    },
    showBoxChart() {
      this.renderChart();
    },
    showLineChart() {
      this.renderChart();
    },
    showOutliers() {
      this.renderChart();
    },
    showYAxisFromZero() {
      this.renderChart();
    },
  },
  created() {
    this.checkForUpdate(this.variable, this.dataset);
  },
  methods: {
    ...mapActions('datasetManager', {
      getVariableData: actions.GET_VARIABLE_DATA,
    }),
    checkForUpdate(v, dataset) {
      if (!v || !dataset) return;
      let varAndDataset = v.id + ':' + dataset.id;
      if (varAndDataset != this.lastVarAndDataset) {
        this.lastVarAndDataset = varAndDataset;
        this.$nextTick(() => this.initialize());
      }
    },
    async initialize() {
      this.isLoading = true;
      this.data = await this.getVariableData({
        datasetId: this.dataset.id,
        varId: this.variable.id,
      });
      this.data = this.data[this.variable.type][this.variable.id];
      this.cache = {};

      if (!this.dataset.longitudinal || this.variable.type == 'subject') {
        this.data = this.flatten(this.data);

        if (this.variable.data_category == 'Continuous') {
          this.cache['x'] = this.data;
        } else {
          // compute unique and counts
          let res = this.data.reduce((acc, x) => {
            acc[x] = acc[x] + 1 || 1;
            return acc;
          }, {});
          this.cache['x'] = Object.keys(res);
          this.cache['y'] = Object.values(res);
        }
      } else {
        this.data = this.flattenAndGroupByVisit(this.data);
        this.visits = sortVisitEvents(Object.keys(this.data));

        if (this.variable.data_category == 'Continuous') {
          this.cache = {
            box: {
              x: [],
              y: [],
            },
            line: {
              x: [],
              y: [],
            },
          };

          for (const v of this.visits) {
            let d = this.data[v];
            this.cache['box']['x'].push(...Array(d.length).fill(v));
            this.cache['box']['y'].push(...d);
            this.cache['line']['x'].push(v);
            this.cache['line']['y'].push(mean(d));
          }
        } else {
          let groups = {};

          for (const v of this.visits) {
            for (const d of this.data[v]) {
              if (!groups[d]) {
                groups[d] = [];
              }
              let entry = groups[d].find(e => e.visit_event == v);
              if (entry) {
                entry.count += 1;
              } else {
                groups[d].push({ visit_event: v, count: 1 });
              }
            }
          }

          for (const [k, entries] of Object.entries(groups)) {
            this.cache[k] = {
              x: entries.map(e => e.visit_event),
              y: entries.map(e => e.count),
            };
          }
        }

        if (this.hideOutlierVisits) {
          var cutoff = 0.1 * this.dataset.num_subjects;

          this.visits = sortVisitEvents(
            Object.entries(this.data)
              .filter(([_, data]) => data.length >= cutoff)
              .map(([visit]) => visit)
          );
        }
      }

      await this.renderChart();

      this.isLoading = false;
    },
    getVarName() {
      return this.useLongScaleNames
        ? this.variable.scale
        : this.variable.abbreviation;
    },
    flatten(data) {
      let res = [];
      if (data != null) {
        data.forEach(d => {
          res.push(d['value']);
        });
      }
      return res;
    },
    flattenAndGroupByVisit(data) {
      var visitKey = 'visit_event';
      var res = {};
      data.forEach(d => {
        var visit = d[visitKey];
        if (!res[visit]) {
          res[visit] = [];
        }
        res[visit].push(d['value']);
      });
      return res;
    },
    close() {
      this.$emit('close');
    },
    resetChart() {
      this.chartData = [];
      this.chartLayout = null;
    },
    async renderChart() {
      this.resetChart();

      // determine type of plot to render
      if (!this.dataset.longitudinal || this.variable.type == 'subject') {
        if (this.variable.data_category == 'Continuous') {
          this.renderHistogramChart();
        } else if (this.variable.data_category == 'Categorical') {
          this.renderBarChart();
        }
      } else {
        if (this.variable.data_category == 'Continuous') {
          if (this.showViolinChart) {
            this.renderViolinChart();
          } else if (this.showBoxChart) {
            this.renderBoxChart();
          } else if (this.showLineChart) {
            this.renderLineChart();
          }
        } else if (this.variable.data_category == 'Categorical') {
          this.renderGroupedBarChart();
        }
      }
    },
    renderHistogramChart() {
      this.chartData = [
        {
          x: this.cache['x'],
          histnorm: 'count',
          type: 'histogram',
          marker: {
            line: {
              color: 'white',
              width: 1,
            },
          },
        },
      ];

      this.renderChartLayout();
    },
    renderBarChart() {
      this.chartData = [
        {
          x: this.cache['x'],
          y: this.cache['y'],
          type: 'bar',
        },
      ];

      this.renderChartLayout();
    },
    renderViolinChart() {
      this.chartData = [
        {
          x: this.cache['box']['x'],
          y: this.cache['box']['y'],
          type: 'violin',
          points: this.showOutliers,
          box: {
            visible: this.showBoxChart,
          },
          meanline: {
            visible: true,
          },
          line: {
            color: this.colors['plotly_palette'][0],
            width: 2,
          },
        },
      ];

      if (this.showLineChart) {
        this.chartData.push(this.getLineChartData());
      }

      this.renderChartLayout();
    },
    renderBoxChart() {
      this.chartData = [
        {
          x: this.cache['box']['x'],
          y: this.cache['box']['y'],
          type: 'box',
          boxmean: true,
          boxpoints: this.showOutliers,
          line: {
            color: this.colors['plotly_palette'][0],
            width: 2,
          },
        },
      ];

      if (this.showLineChart) {
        this.chartData.push(this.getLineChartData());
      }

      this.renderChartLayout();
    },
    getLineChartData() {
      return {
        x: this.cache['line']['x'],
        y: this.cache['line']['y'],
        type: 'scatter',
        mode: 'lines+markers',
        line: {
          color: this.colors['plotly_palette'][0],
          width: 3,
        },
        marker: {
          size: 12,
        },
      };
    },
    renderLineChart() {
      this.chartData = [this.getLineChartData()];
      this.renderChartLayout();
    },
    renderGroupedBarChart() {
      for (const [k, v] of Object.entries(this.cache)) {
        let chartData = {
          x: v['x'],
          y: v['y'],
          name: k,
          type: 'bar',
          marker: {
            line: {
              color: 'white',
              width: 1,
            },
          },
        };

        this.chartData.push(chartData);
      }

      this.renderChartLayout();
    },
    renderChartLayout() {
      let chartLayout = {
        showlegend: false,
        responsive: true,
        autosize: true,
        height: 500,
        legend: {
          y: 0.95,
        },
        margin: {
          t: 30,
          l: 80,
          r: 40,
          b: 60,
        },
        yaxis: {
          zeroline: false,
          automargin: true,
          fixedrange: true,
          title: {
            text: this.yAxisLabel,
          },
        },
        xaxis: {
          zeroline: false,
          automargin: true,
          rangeslider: {
            thickness: 0.05,
            bgcolor: 'lightgray',
          },
          title: {
            text: this.xAxisLabel,
          },
        },
        hoverlabel: {
          bgcolor: '#ffffff',
          margin: {
            l: 10,
          },
          font: {
            family: 'Roboto, sans-serif',
            size: 16,
          },
        },
        font: {
          family: 'Roboto, sans-serif',
          size: 16,
        },
        modebar: {
          remove: ['lasso', 'select', 'pan'],
        },
      };
      if (this.showYAxisFromZero) {
        let yext = null;
        this.chartData.forEach(cd => {
          if ('y' in cd) {
            let nyext = extent(cd['y'].map(y => y * 1.0));
            if (yext == null) {
              yext = nyext;
            } else {
              if (nyext[0] < yext[0]) {
                yext[0] = nyext[0];
              }
              if (nyext[1] > yext[1]) {
                yext[1] = nyext[1];
              }
            }
          }
        });
        if (yext != null && yext[0] > 0) {
          var new_ymax = yext[1] * 1.0 + (yext[1] - yext[0]) * 0.25;
          if (new_ymax > 0) {
            chartLayout['yaxis']['range'] = [0, new_ymax];
          }
        }
      }
      this.chartLayout = chartLayout;
    },
  },
};
</script>

<style scoped>
.v-divider {
  border-color: gray !important;
}
</style>
